import { useState, useEffect } from "react";
import { useRouter } from "next/router";

import {
  useCreateCheckoutMutation,
  useGetAddressQuery,
  useGetCartQuery,
} from "../../../store";
import { AddressType } from "../../../types/address_types";
import { AddressMain } from "./AddressMain";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
} from "@mui/material";
import { useTypedSelector } from "../../../hooks/useTypedSelector";
import { analytics } from "../../analytics";
import { CartProductEntity } from "../../../types/cart_response_type";
import { AuthLayout } from "../../layouts";
import withAuth from "../../hoc/withAuth";

export const Address = withAuth<{}>(() => {
  const router = useRouter();
  const { data } = useGetAddressQuery();

  const [isStorePickup, setIsStorePickup] = useState(false);
  const isLoggedin = useTypedSelector(state => state.auth.isLoggedin);
  const { data: cartData } = useGetCartQuery(undefined, { skip: !isLoggedin });
  const [createCheckout, { data: checkoutData, isLoading, isSuccess }] =
    useCreateCheckoutMutation();

  useEffect(() => {
    if (isSuccess && checkoutData && cartData) {
      const currency =
        shippingAddress!.country.toLowerCase() === "india" ? "INR" : "USD";
      const priceItem =
        currency === "INR" ? cartData.value[1] : cartData.value[0];
      analytics.pushBeginCheckout({
        eventId: checkoutData.eventId,
        products: cartData.products.filter(
          p => !p.hasOwnProperty("errMessage")
        ) as CartProductEntity[],
        totalValue: priceItem.sellingPrice,
        cartId: cartData.cartId,
      });
      analytics.pushAddShippingInfo({
        eventId: checkoutData.eventId,
        address: shippingAddress!,
        products: cartData.products.filter(
          p => !p.hasOwnProperty("errMessage")
        ) as CartProductEntity[],
        totalValue: priceItem.sellingPrice,
      });
      router.push(
        `/checkout/payment?billingAddressId=${
          billingAddress!.id
        }&shippingAddressId=${shippingAddress!.id}&currency=${
          shippingAddress!.country.toLowerCase() === "india" ? "INR" : "USD"
        }`
      );
    }
  }, [isSuccess, checkoutData, cartData]);

  const [shippingAddress, setShippingAddress] = useState<AddressType>();
  const [billingAddress, setBillingAddress] = useState<AddressType>();
  const [isSameAddress, setIsSameAddress] = useState(true);

  const selectShippingAddress = (address: AddressType) => {
    if (isSameAddress) {
      setShippingAddress(address);
      setBillingAddress(address);
    } else {
      setShippingAddress(address);
    }
  };

  const selectBillingAddress = (address: AddressType) =>
    setBillingAddress(address);

  const handleProceed = async () => {
    if (shippingAddress && billingAddress) {
      createCheckout({
        method: "POST",
        payload: {
          billingAddressId: billingAddress.id,
          paymentMethodId: 1,
          priceruleId: 0,
          shippingAddressId: shippingAddress.id,
        },
      });
    }
  };

  const handleChangeSameAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked) {
      setBillingAddress(undefined);
    } else {
      setBillingAddress(shippingAddress);
    }
    setIsSameAddress(e.target.checked);
  };

  return (
    <AuthLayout
      title="Select Address"
      subtitle={
        router.query["store-pickup-available"] === "true" ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={isStorePickup}
                onChange={e => {
                  if (e.target.checked) {
                    setShippingAddress({
                      note: "",
                      addressLineOne:
                        "No 460, Sai Ishwar Krupa, 7th Main Road, NGEF Layout, Sadanandanagara",
                      state: "Karnataka",
                      fullName: "Ninjaas Labs Private Limited",
                      country: "India",
                      companyName: "",
                      createdAt: 1677843201,
                      landmark: "Near Baipanahalli Metro Station",
                      longitude: "",
                      emailId: "devteam@jumkey.com",
                      mobile: "919902631591",
                      city: "Bangalore",
                      modifiedAt: 1677843201,
                      isDefaultAddress: false,
                      addressType: "HOME",
                      zipcode: "560038",
                      id: 33610,
                      addressId: 33610,
                      latitude: "",
                      addressLineTwo: "Indiranagar",
                      customerId: 0,
                    });
                  } else {
                    setShippingAddress(undefined);
                    setBillingAddress(undefined);
                  }
                  setIsStorePickup(e.target.checked);
                }}
              />
            }
            label="Store pick up"
          />
        ) : null
      }>
      {data && (
        <div className="space-y-8">
          {!isStorePickup ? (
            <>
              <AddressMain
                title="Shipping Address"
                address={data.filter(d => {
                  if (!isSameAddress) {
                    if (billingAddress) {
                      return billingAddress.id !== d.id;
                    }
                  }
                  return true;
                })}
                selectedAddressId={shippingAddress?.id}
                selectAddress={selectShippingAddress}
              />
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSameAddress}
                      onChange={handleChangeSameAddress}
                    />
                  }
                  label="Shipping address same as billing address."
                />
              </div>
            </>
          ) : null}

          {(!isSameAddress || isStorePickup) && (
            <AddressMain
              title="Billing Address"
              address={data.filter(d => shippingAddress?.id !== d.id)}
              selectedAddressId={billingAddress?.id}
              selectAddress={selectBillingAddress}
            />
          )}
          <div className="sticky bottom-0 py-2 z-10">
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleProceed}
              startIcon={isLoading ? <CircularProgress size={16} /> : null}
              disabled={
                !(Boolean(shippingAddress) && Boolean(billingAddress)) ||
                isLoading
              }>
              Proceed for payment
            </Button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
});
